import { useEffect } from 'react';
import { XCircle, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AdminPaymentSummary } from '../types/payment';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { formatDate } from '../utils/formatDate';

interface PaymentDetailPanelProps {
  readonly payment: AdminPaymentSummary | null;
  readonly onClose: () => void;
}

/** Formats a numeric amount with a best-effort currency symbol. */
const formatAmount = (amount: number, currency: string): string => {
  const SYMBOLS: Record<string, string> = { usd: '$', eur: '€', gbp: '£' };
  const symbol = SYMBOLS[currency.toLowerCase()] ?? currency.toUpperCase();
  return `${symbol}${amount.toFixed(2)}`;
};

export const PaymentDetailPanel = ({ payment, onClose }: PaymentDetailPanelProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);

  // Escape key closes the panel
  useEffect(() => {
    if (!payment) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [payment, onClose]);

  if (!payment) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-10"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        className="fixed inset-y-0 right-0 w-96 bg-white border-l border-gray-200 shadow-xl flex flex-col z-20"
        aria-label={t('pages:paymentDetailsAriaLabel')}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{t('pages:paymentDetailsHeading')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={t('pages:closePanel')}
          >
            <XCircle size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="space-y-3 text-sm">

            <div className="flex items-start justify-between gap-4">
              <span className="text-gray-500 shrink-0">{t('pages:fieldPaymentId')}</span>
              <span className="font-mono text-gray-900 text-right break-all">{payment.paymentId}</span>
            </div>

            <div className="flex items-start justify-between gap-4">
              <span className="text-gray-500 shrink-0">{t('pages:fieldApartment')}</span>
              <span className="text-gray-700 text-right">{payment.apartmentName}</span>
            </div>

            <div className="flex items-start justify-between gap-4">
              <span className="text-gray-500 shrink-0">{t('pages:fieldGuest')}</span>
              <span className="text-gray-700 text-right">
                {payment.guestFirstName} {payment.guestLastName}
              </span>
            </div>

            <div className="flex items-start justify-between gap-4">
              <span className="text-gray-500 shrink-0">{t('pages:fieldEmail')}</span>
              <span className="text-gray-600 text-right break-all">{payment.guestEmail}</span>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-500 shrink-0">{t('pages:fieldStatus')}</span>
              <PaymentStatusBadge status={payment.status} />
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-500 shrink-0">{t('pages:fieldAmount')}</span>
              <span className="font-medium text-gray-900">
                {formatAmount(payment.amount, payment.currency)}
              </span>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-500 shrink-0">{t('pages:fieldCreated')}</span>
              <span className="text-gray-700">{formatDate(payment.createdAt.slice(0, 10))}</span>
            </div>

          </div>
        </div>

        {/* Footer — View in Stripe */}
        <div className="px-6 py-4 border-t border-gray-100">
          {payment.stripeManagementUrl ? (
            <a
              href={payment.stripeManagementUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ExternalLink size={14} />
              {t('pages:viewInStripe')}
            </a>
          ) : (
            <button
              disabled
              title={t('pages:stripeIdUnavailable')}
              className="flex items-center justify-center gap-2 w-full bg-gray-100 text-gray-400 text-sm font-medium px-4 py-2 rounded-lg cursor-not-allowed"
            >
              <ExternalLink size={14} />
              {t('pages:viewInStripe')}
            </button>
          )}
        </div>
      </aside>
    </>
  );
};
